import { httpMethods } from '@/api';
import { sendApiRequest } from '@/api/sendApiRequest';
import {
  TContractorsResponse,
  TContractorAddress,
  TContractorAddressPayload,
  TContractorAddressEmails,
  TContractorByEntryUidResponse,
  TSetCheckmarkCreateRequest,
  TContractorIncidentResponse,
  TContractorsRequest,
} from '@/contexts/contractorsContext/domain/types';
import { TDriverResponse } from '@/contexts/digitalQueuesContext/domain/types';
import {
  TPagination,
  TResponseWithId,
} from '@/types';
import { TCustomizationFields } from '@/domains/types';
import { TContractorInfo } from '@/components/layouts/domain/types';
import { TCustomTableSort } from '@/components/CustomTableLayout/types';
import { TRunInfo } from '@/contexts/runExecutionContext/types';

export const fetchContractors = ({
  pageItems,
  currentPage,
  category,
  searchQuery,
} = {
  pageItems: null,
  currentPage: null,
  category: null,
  searchQuery: null,
} as TContractorsRequest,
) => sendApiRequest<TContractorsResponse>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/contractors',
  params: {
    items: pageItems,
    page: currentPage,
    category,
    searchQuery,
  },
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

export const fetchContractor = ({ entryUid }: { entryUid: string }) => sendApiRequest<TContractorByEntryUidResponse>({
  method: httpMethods.GET,
  endpoint: `/contractors/${entryUid}`,
  withoutAuth: true,
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

export const getContractorAddressInfo = (contractorId: string, addressId: string) => sendApiRequest<any>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/contractors/${contractorId}/addresses/${addressId}`,
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

export const sendEmailWithCredentials = ({
  contractorId,
  addressId,
  notificationEmails,
}: {
  contractorId: string,
  addressId: string,
  notificationEmails: TContractorAddressEmails,
}) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/contractors/${contractorId}/addresses/${addressId}/notification_emails/send_credentials`,
  payload: { notificationEmails },
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

export const unsubscribeUser = ({ entryUid, email }: { entryUid: string, email: string }) => sendApiRequest({
  method: httpMethods.POST,
  endpoint: '/as_contractor/unsubscribe',
  params: {
    entryUid,
    email,
  },
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

export function fetchRuns(
  { pageItems, currentPage }: TPagination,
  { sort, sortBy }: TCustomTableSort,
  filters: any,
  withIncidentsOnTop = false,
) {
  return sendApiRequest({
    method: httpMethods.GET,
    endpoint: '/:tenant_id/as_contractor/runs',
    params: {
      items: pageItems,
      page: currentPage,
      sort,
      sortBy,
      withIncidentsOnTop,
      ...filters,
    },
  });
}

export function getDriversByNameQuery(name: string) {
  return sendApiRequest<TDriverResponse[]>({
    method: httpMethods.GET,
    endpoint: '/:tenant_id/autocomplete/as_contractor/run_drivers',
    params: { name },
  });
}

export const removeContractor = (contractorId: string) => sendApiRequest({
  method: httpMethods.DELETE,
  endpoint: `/:tenant_id/contractors/${contractorId}`,
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

export const addAddressToContractor = ({
  contractorId,
  address,
  notificationEmails,
  isExport,
  externalDigitalQueueId,
  externalDigitalQueueSettingsAttributes,
}: {
  contractorId: string,
  address: any,
  notificationEmails?: TContractorAddressEmails,
  isExport: boolean,
  externalDigitalQueueId?: string,
  externalDigitalQueueSettingsAttributes?: TCustomizationFields,
}) => {
  const {
    id: addressId,
    description,
    ...addressAttributes
  } = address;

  const payload: TContractorAddressPayload = {
    description,
    notificationEmails,
    export: isExport,
    externalDigitalQueueId,
    externalDigitalQueueSettingsAttributes,
  };

  if (addressId) {
    payload.addressId = addressId;
  } else {
    payload.addressAttributes = addressAttributes;
  }

  return sendApiRequest({
    method: httpMethods.POST,
    endpoint: `/:tenant_id/contractors/${contractorId}/addresses`,
    payload,
  });
};

export const editAddressContacts = ({
  contractorId,
  addressId,
  contacts,
}: {
  contractorId: string,
  addressId: string,
  contacts: any,
}) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/contractors/${contractorId}/addresses/${addressId}/contacts`,
  payload: { contacts },
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

export const editContactsEmails = ({
  contractorId,
  addressId,
  notificationEmails,
}: {
  contractorId: string,
  addressId: string | null,
  notificationEmails: TContractorAddressEmails,
}) => sendApiRequest({
  method: httpMethods.PATCH,
  endpoint: `/:tenant_id/contractors/${contractorId}/addresses/${addressId}/notification_emails`,
  payload: { notificationEmails },
});

export const getContractorAddresses = (contractorId: string) => sendApiRequest<TContractorAddress[]>({
  method: httpMethods.GET,
  endpoint: `/:tenant_id/contractors/${contractorId}/addresses`,
});

export function fetchRunInfo(uid: string) {
  return sendApiRequest<TRunInfo>({
    method: httpMethods.GET,
    endpoint: `/:tenant_id/as_contractor/runs/${uid}`,
  });
}

export const setCheckmark = (uid: string, payload: TSetCheckmarkCreateRequest) => sendApiRequest<TRunInfo>({
  method: httpMethods.POST,
  endpoint: `/:tenant_id/as_contractor/runs/${uid}/checkmarks`,
  payload,
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

export const updateContractorExternalNumber = (
  contractorId: string,
  externalNumber: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/contractors/${contractorId}/external_number`,
  payload: { externalNumber },
});

export const updateContractorInn = (
  contractorId: string,
  inn: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/contractors/${contractorId}/inn`,
  payload: { inn },
});

export const updateContractorKpp = (
  contractorId: string,
  kpp: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/contractors/${contractorId}/kpp`,
  payload: { kpp },
});

export const updateContractorName = (
  contractorId: string,
  name: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/contractors/${contractorId}/name`,
  payload: { name },
});

export const updateContractorOgrn = (
  contractorId: string,
  ogrn: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/contractors/${contractorId}/ogrn`,
  payload: { ogrn },
});

export const updateContractorLegalAddress = (
  contractorId: string,
  legalAddress: string,
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/contractors/${contractorId}/legal_address`,
  payload: { legalAddress },
  requestOptions: {
    showError: true,
    serverErrorFirst: true,
  },
});

export const updateContractorCategories = (
  contractorId: string,
  categories: string[],
) => sendApiRequest<TResponseWithId>({
  method: httpMethods.PUT,
  endpoint: `/:tenant_id/contractors/${contractorId}/categories`,
  payload: { categories },
});

export const getRunIncidents = (pagination: TPagination) => sendApiRequest<TContractorIncidentResponse[]>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/as_contractor/runs/incidents',
  params: {
    items: pagination.pageItems,
    page: pagination.currentPage,
  },
});

export const getContractorInfo = () => sendApiRequest<TContractorInfo>({
  method: httpMethods.GET,
  endpoint: '/:tenant_id/as_contractor/info',
});
